import React, {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';
import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  LinearProgress,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import Section from '../../../components/Main/Section/Section';
import ContentContainer from '../../../components/Containers/ContentContainer';
import LeftContainer from '../../../components/Containers/LeftContainer';
import RightContainer from '../../../components/Containers/RightContainer';
import { CanceledError } from 'axios';
import http from '../../../shared/HttpCommon';
import Pagination from '../../../components/Pagination/Pagination';
import { IReviewNew } from '../../../shared/interfaces/IReviewNew';
import SortContainer from '../../../components/Containers/SortContainer';
import SliderStarsAsLabels from '../../../components/Sliders/StarsAsLabels/SliderStarsAsLabels';
import { IMonitoring } from '../../../shared/interfaces/IMonitoring';
import ReviewMonitoringService from '../../../services/ReviewMonitoringService';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import Filter from '../../../components/Filter/Filter';
import PlatformSelect from '../../../components/PlatformSelect/PlatformSelect';
import PlatformService from '../../../services/PlatformService';
import { IPlatform } from '../../../shared/interfaces/IPlatform';
import ReviewNew from '../../../components/Review/ReviewNew';
import Chip from '../../../components/Chip/Chip';
import { format, isValid } from 'date-fns';
import GMBService from '../../../services/GMBService';
import { IGMBLocationsResponse } from '../../../shared/interfaces/IGMBLocationsResponse';
import { IGmbLocation } from '../../../shared/interfaces/IGmbLocation';
import { AuthContext } from '../../../providers/AuthProvider';
import StringUtils from '../../../shared/StringUtils';

interface FilterOptions {
  textSearch?: string;
  commentStatus?: string;
  company?: string;
  endDate?: string;
  kind?: string;
  pageSize: number;
  offset: number;
  orderBy?: string[];
  platform?: string;
  score: number[];
  startDate?: string;
  withComments: boolean;
  withoutComments: boolean;
  isUpdated?: boolean;
  isDeleted?: boolean;
  hasNotes?: boolean;
  hasDeletion?: boolean;
}

interface Paging {
  pagesTotal: number;
  totalItems: number;
}

const defaultFilter: FilterOptions = {
  pageSize: 10,
  offset: 0,
  score: [1, 5],
  withComments: true,
  withoutComments: true,
  commentStatus: 'OPEN',
  orderBy: ['score desc'],
  hasNotes: false,
};

const Commentings: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { setCurrentView } = useContext(CurrentViewContext);
  const { hasRole } = useContext(AuthContext);

  const [localText, setLocalText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [applyFilter, setApplyFilter] = useState(false);
  const [filterByCompanyName, setFilterByCompanyName] = useState<IMonitoring | null>(null);
  const [monitoring, setMonitoring] = useState<IMonitoring[]>([]);
  const [reviews, setReviews] = useState<IReviewNew[]>([]);
  const [platforms, setPlatforms] = useState<IPlatform[]>([]);
  const [locations, setLocations] = useState<IGmbLocation[]>([]);
  const startDateInputRef = useRef<Date | null>(null);
  const endDateInputRef = useRef<Date | null>(null);
  const [page, setPage] = useState<number>(1);
  const [filter, setFilter] = useState<FilterOptions>(defaultFilter);
  const [startDateError, setStartDateError] = useState<boolean>(false);
  const [endDateError, setEndDateError] = useState<boolean>(false);
  const [withDeleted, setWithDeleted] = useState<boolean>(false);
  const [paging, setPaging] = useState<Paging>({
    pagesTotal: 0,
    totalItems: 0,
  });

  const reviewerRoles = [
    {
      key: 'employee',
      name: t('employee'),
    },
    {
      key: 'applicant',
      name: t('applicant'),
    },
  ];

  useEffect(() => {
    setCurrentView(t('nav_commentings'));
    document.title = `Reputativ ${t('nav_commentings')}`;
  }, [setCurrentView, t]);

  // HANDLER FUNCTIONS START

  const handleStartDateChange = () => {
    const value = startDateInputRef.current;
    if (value === null) {
      updateFilter((prevState) => {
        if (!prevState.startDate) {
          return prevState;
        } else {
          const newState = { ...prevState, offset: 0 };
          delete newState['startDate'];
          return newState;
        }
      });
    } else {
      if (!isValid(value)) {
        setStartDateError(true);
        return;
      }
      updateFilter((prevState) => {
        if (prevState.startDate === new Date(value).toISOString()) {
          return prevState;
        } else {
          return {
            ...prevState,
            offset: 0,
            startDate: new Date(value).toISOString(),
          };
        }
      });
    }
  };

  const handleEndDateChange = () => {
    const value = endDateInputRef.current;
    if (value === null) {
      updateFilter((prevState) => {
        if (!prevState.endDate) {
          return prevState;
        } else {
          const newState = { ...prevState, offset: 0 };
          delete newState['endDate'];
          return newState;
        }
      });
    } else {
      if (!isValid(value)) {
        setEndDateError(true);
        return;
      }
      updateFilter((prevState) => {
        if (prevState.endDate === new Date(value).toISOString()) {
          return prevState;
        } else {
          return {
            ...prevState,
            offset: 0,
            endDate: new Date(value).toISOString(),
          };
        }
      });
    }
  };

  const updateFilter = (fn: React.SetStateAction<FilterOptions>, page?: number) => {
    if (!page) {
      page = 1;
    }
    setFilter(fn);
    setPage(page);
  };

  const handleWithDeletedChange = () => {
    setWithDeleted((prevState) => !prevState);
    updateFilter((prevState) => {
      return {
        ...prevState,
        offset: 0,
        isDeleted: !prevState.isDeleted,
      };
    });
  };

  // HANDLER FUNCTIONS END

  // USE EFFECT PLATFORMS AND MONITORING START

  useEffect(() => {
    const controller = new AbortController();

    const getPlatforms = async () => {
      const response = await PlatformService.getPlatforms(setLoading, controller.signal);
      if (!Array.isArray(response)) return;

      setPlatforms(response);
    };
    const getMonitoring = async () => {
      const gmbMonitoredLocations: IGMBLocationsResponse = await GMBService.getAllLocations(
        undefined,
        1000,
        setLoading,
        controller.signal,
        true
      );

      if (!gmbMonitoredLocations) return;
      setLocations(gmbMonitoredLocations.locations);

      let monitoringEntries: IMonitoring[] = await ReviewMonitoringService.getMonitoring(
        false,
        'reviews',
        setLoading,
        controller.signal
      );

      if (!monitoringEntries) return;

      const hasGmbEntries = gmbMonitoredLocations.locations.length > 0;

      for (const location of gmbMonitoredLocations.locations) {
        const mon: IMonitoring = {
          company: location.locationID,
          createdAt: '',
          deletedAt: '',
          id: location.ID,
          jobs: [],
          platformId: -2,
          label: location.locationData.title,
          subLabel: `${location.locationData.address.addressLines}, ${location.locationData.address.postalCode} ${location.locationData.address.locality}`,
          url: '',
          updatedAt: '',
          communityManagement: location.communityManagement,
          isBenchmark: false,
          type: 'LOCATION',
        };
        monitoringEntries.push(mon);
      }

      if (hasGmbEntries) {
        setPlatforms((prevState) => [...prevState, PlatformService.getPseudoGoogle()]);
      }

      setMonitoring(monitoringEntries);
      setPlatforms((prevState) => {
        return [...prevState].filter((v) => monitoringEntries.find((m) => m.platformId === v.id));
      });
    };

    setLoading(true);

    (async () => {
      await getPlatforms();
      await getMonitoring();
      loadAllReviews();
    })();

    return () => {
      controller.abort();
    };
  }, []);

  const loadAllReviews = () => {
    setApplyFilter(true);
  };

  useEffect(() => {
    if (!applyFilter) return;

    const controller = new AbortController();
    const getReviews = async () => {
      try {
        setLoading(true);
        const { data } = await http.post('/portal/comments/reviews', filter, {
          signal: controller.signal,
        });
        if (!data) {
          setLoading(false);
          setApplyFilter(false);
          return;
        } else {
          setLoading(false);
          setApplyFilter(false);
          const reviews = data.reviews ?? [];
          setReviews(reviews);
          setPaging(data.paging);
        }
      } catch (error) {
        if (error instanceof CanceledError) {
        } else {
          setLoading(false);
          setApplyFilter(false);
          console.error(error);
        }
      }
    };
    getReviews().catch((error) => console.log(error));

    setLoading(true);
  }, [applyFilter]);

  const renderedLocationFilterName = (props: any, option: any, state: any, ownerState: any) => {
    const { key, ...optionProps } = props;
    return (
      <Box
        key={key}
        component="li"
        {...optionProps}
      >
        <Box>
          <Typography>{option.label}</Typography>
          {option.subLabel && (
            <Typography sx={{ fontSize: '10px' }}>{`(${option.subLabel})`}</Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Section>
      <ContentContainer>
        <LeftContainer>
          <SortContainer sx={{ mt: 0 }}>
            <Typography variant="body2">
              {paging.totalItems > 1
                ? `${paging.totalItems} ${t('reviews')}`
                : `${paging.totalItems} ${t('review')}`}
            </Typography>
            <FormControl
              size="small"
              sx={{ minWidth: '284px' }}
            >
              <InputLabel id="sort-select-label">{t('sort')}</InputLabel>
              <Select
                labelId="sort-select-label"
                value={filter.orderBy || []}
                input={<OutlinedInput label={t('sort')} />}
                onChange={(event) => {
                  const {
                    target: { value },
                  } = event;
                  const values = typeof value === 'string' ? value.split(',') : value;

                  function removeConflictingSelection(conflictFirst: any, conflictSecond: any) {
                    const isSelectedA = values.includes(conflictFirst);
                    const isSelectedB = values.includes(conflictSecond);

                    if (isSelectedA && isSelectedB) {
                      const lastValue = values[values.length - 1];
                      if (lastValue === conflictFirst || lastValue === conflictSecond) {
                        values.pop();
                      }
                    }
                  }

                  removeConflictingSelection('score asc', 'score desc');
                  removeConflictingSelection('date asc', 'date desc');

                  const validCombinations = new Set([
                    'score asc',
                    'score desc',
                    'date asc',
                    'date desc',
                  ]);
                  const filteredValues = values.filter((value) => validCombinations.has(value));

                  if (filteredValues.length > 0) {
                    updateFilter((prevState) => ({
                      ...prevState,
                      offset: 0,
                      orderBy: filteredValues,
                    }));
                  } else {
                    updateFilter((prevState) => {
                      const newState = { ...prevState, offset: 0 };
                      delete newState['orderBy'];
                      return newState;
                    });
                  }
                  loadAllReviews();
                }}
                label={t('sort')}
              >
                <MenuItem value="score asc">{t('score_asc')}</MenuItem>
                <MenuItem value="score desc">{t('score_desc')}</MenuItem>
                <MenuItem value="date asc">{t('date_asc')}</MenuItem>
                <MenuItem value="date desc">{t('date_desc')}</MenuItem>
              </Select>
            </FormControl>
          </SortContainer>
          {loading ? (
            <Box>
              <LinearProgress sx={{ my: theme.spacing(1) }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <ReviewNew
                reviews={reviews}
                monitoring={monitoring}
                commentingModule={true}
                location={locations}
                hideHeader={false}
                hideFooter={false}
              />
              <Pagination
                count={paging.pagesTotal}
                page={page}
                itemDescription={t('reviews')}
                showItemsPerPage={String(filter.pageSize)}
                onPageChange={(event: ChangeEvent<unknown>, pageNumber: number) => {
                  updateFilter(
                    (prevState) => ({
                      ...prevState,
                      offset: prevState.pageSize * pageNumber - prevState.pageSize,
                    }),
                    pageNumber
                  );
                  loadAllReviews();
                }}
                onItemsPerPageChange={(event: SelectChangeEvent) => {
                  updateFilter(
                    (prevState) => ({
                      ...prevState,
                      pageSize: parseInt(event.target.value),
                      offset: 0,
                    }),
                    1
                  );
                  loadAllReviews();
                  setPaging({
                    pagesTotal: 0,
                    totalItems: 0,
                  });
                }}
              />
            </Box>
          )}
        </LeftContainer>
        <RightContainer>
          <Filter
            filters={
              <>
                <FormControl
                  disabled={loading}
                  size="small"
                  sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                >
                  <InputLabel id="comment-status-select-label">Status</InputLabel>
                  <Select
                    labelId="comment-status-select-label"
                    value={filter.commentStatus || ''}
                    onChange={(event: SelectChangeEvent) => {
                      if (event.target.value === '') {
                        updateFilter((prevState) => {
                          const newState = { ...prevState, offset: 0 };
                          delete newState['commentStatus'];
                          return newState;
                        });
                      } else {
                        updateFilter((prevState) => ({
                          ...prevState,
                          offset: 0,
                          commentStatus: event.target.value,
                        }));
                      }
                    }}
                    label="Status"
                  >
                    <MenuItem value="">
                      <em>-</em>
                    </MenuItem>
                    <MenuItem value="OPEN">{t('OPEN')}</MenuItem>
                    <MenuItem value="APPROVAL">{t('APPROVAL')}</MenuItem>
                    <MenuItem value="DONE">{t('DONE')}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl
                  disabled={loading}
                  size="small"
                  sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                >
                  <Autocomplete
                    value={filterByCompanyName}
                    getOptionLabel={(m: any) => m.label}
                    getOptionKey={(m: any) => `${m.company}_${m.platformId}`}
                    options={monitoring}
                    filterOptions={createFilterOptions({
                      stringify: (option: any) => `${option.label} ${option.subLabel}`,
                    })}
                    renderOption={renderedLocationFilterName}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label={t('monitoring_label')}
                      />
                    )}
                    onChange={(event: any, newValue: any) => {
                      setFilterByCompanyName(newValue);
                      if (!newValue) {
                        updateFilter((prevState) => {
                          const newState = { ...prevState, offset: 0 };
                          delete newState['company'];
                          delete newState['platform'];
                          return newState;
                        });
                      } else {
                        const platformName = PlatformService.getPlatformName(
                          newValue.platformId,
                          platforms
                        );
                        updateFilter((prevState) => ({
                          ...prevState,
                          offset: 0,
                          company: newValue.company,
                          platform: platformName,
                        }));
                      }
                    }}
                  />
                </FormControl>
                <TextField
                  label={t('search')}
                  variant="outlined"
                  value={localText}
                  size="small"
                  sx={{ mt: theme.spacing(2) }}
                  onChange={(event) => {
                    setLocalText(event.target.value);
                    updateFilter((prevState) => ({
                      ...prevState,
                      offset: 0,
                      textSearch: StringUtils.toFilterText(event.target.value),
                    }));
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ textTransform: 'uppercase', fontWeight: '700', mt: theme.spacing(2) }}
                >
                  {t('score')}
                </Typography>
                <SliderStarsAsLabels
                  value={filter.score}
                  onChange={(event: Event, newValue: number | number[]) => {
                    updateFilter((prevState) => {
                      const newScore = Array.isArray(newValue) ? newValue : [newValue, newValue];
                      if (JSON.stringify(prevState.score) === JSON.stringify(newScore)) {
                        return prevState;
                      } else {
                        return {
                          ...prevState,
                          offset: 0,
                          score: newScore,
                        };
                      }
                    });
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    fontWeight: '700',
                    mt: theme.spacing(2),
                  }}
                >
                  {t('date')}
                </Typography>
                <DesktopDatePicker
                  disabled={loading}
                  disableFuture
                  label={t('date_from')}
                  format="dd.MM.yyyy"
                  value={filter.startDate ? new Date(filter.startDate) : null}
                  onChange={(newValue) => {
                    startDateInputRef.current = newValue;
                    setStartDateError(false);
                  }}
                  onClose={handleStartDateChange}
                  slotProps={{
                    textField: {
                      onBlur: handleStartDateChange,
                      onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === 'Enter') {
                          handleStartDateChange();
                        }
                      },
                      size: 'small',
                      sx: { my: theme.spacing(1) },
                      error: startDateError,
                    },
                  }}
                />
                <DesktopDatePicker
                  disabled={loading}
                  disableFuture
                  label={t('date_to')}
                  format="dd.MM.yyyy"
                  value={filter.endDate ? new Date(filter.endDate) : null}
                  onChange={(newValue) => {
                    endDateInputRef.current = newValue;
                    setEndDateError(false);
                  }}
                  onClose={handleEndDateChange}
                  slotProps={{
                    textField: {
                      onBlur: handleEndDateChange,
                      onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === 'Enter') {
                          handleEndDateChange();
                        }
                      },
                      size: 'small',
                      sx: { my: theme.spacing(1) },
                      error: endDateError,
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    fontWeight: '700',
                    mt: theme.spacing(2),
                  }}
                >
                  {t('platform')}
                </Typography>
                <PlatformSelect
                  disabled={loading}
                  value={filter.platform || ''}
                  onChange={(event: SelectChangeEvent) => {
                    const platformName = event.target.value || '';
                    if (platformName === '') {
                      updateFilter((prevState) => {
                        const newState = { ...prevState, offset: 0 };
                        delete newState['platform'];
                        delete newState['company'];
                        delete newState['kind'];
                        return newState;
                      });
                    } else {
                      updateFilter((prevState) => {
                        const newState = { ...prevState, offset: 0, platform: platformName };
                        delete newState['company'];
                        delete newState['kind'];
                        return newState;
                      });
                    }
                  }}
                  platforms={platforms}
                />
                {filter.platform === 'kununu' && (
                  <FormControl
                    disabled={loading}
                    size="small"
                    sx={{ minWidth: '284px', my: theme.spacing(1) }}
                  >
                    <InputLabel id="kununu-reviewer-role-filter-label">
                      {t('reviewer_role')}
                    </InputLabel>
                    <Select
                      labelId="kununu-reviewer-role-filter-label"
                      value={filter.kind || ''}
                      onChange={(event: SelectChangeEvent) => {
                        if (event.target.value === '') {
                          updateFilter((prevState) => {
                            const newState = { ...prevState, offset: 0 };
                            delete newState['kind'];
                            return newState;
                          });
                        } else {
                          updateFilter((prevState) => ({
                            ...prevState,
                            kind: event.target.value,
                          }));
                        }
                      }}
                      label={t('reviewer_role')}
                    >
                      <MenuItem value="">
                        <em>-</em>
                      </MenuItem>
                      {reviewerRoles.map(({ key, name }) => {
                        return (
                          <MenuItem
                            key={key}
                            value={key}
                          >
                            {name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                )}
                <FormGroup sx={{ my: theme.spacing(1) }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filter.withComments}
                        onChange={() => {
                          if (filter.withComments && !filter.withoutComments) {
                            updateFilter((prevState) => ({
                              ...prevState,
                              offset: 0,
                              withComments: true,
                              withoutComments: true,
                            }));
                          } else {
                            updateFilter((prevState) => ({
                              ...prevState,
                              offset: 0,
                              withComments: !prevState.withComments,
                            }));
                          }
                        }}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    }
                    label={<Typography variant="body2">{t('reviews_with_comment')}</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filter.withoutComments}
                        onChange={() => {
                          if (!filter.withComments && filter.withoutComments) {
                            updateFilter((prevState) => ({
                              ...prevState,
                              offset: 0,
                              withComments: true,
                              withoutComments: true,
                            }));
                          } else {
                            updateFilter((prevState) => ({
                              ...prevState,
                              offset: 0,
                              withoutComments: !prevState.withoutComments,
                            }));
                          }
                        }}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    }
                    label={<Typography variant="body2">{t('reviews_without_comment')}</Typography>}
                  />
                  {hasRole('can-use-deletion') && (
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={filter.hasDeletion}
                            onChange={() => {
                              updateFilter((prevState) => ({
                                ...prevState,
                                offset: 0,
                                hasDeletion: !prevState.hasDeletion,
                              }));
                            }}
                            inputProps={{ 'aria-label': 'controlled' }}
                          />
                        }
                        label={<Typography variant="body2">{t('reviews_to_delete')}</Typography>}
                      />
                    </FormGroup>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filter.isUpdated}
                        onChange={() => {
                          updateFilter((prevState) => {
                            const newState = { ...prevState, offset: 0 };
                            if (prevState.isUpdated) {
                              delete newState.isUpdated;
                            } else {
                              newState.isUpdated = true;
                            }
                            return newState;
                          });
                        }}
                      />
                    }
                    label={<Typography variant="body2">{t('updated_ratings')}</Typography>}
                  />
                  {hasRole('can-use-note') && (
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={filter.hasNotes}
                            onChange={() => {
                              updateFilter((prevState) => {
                                const newState = { ...prevState, offset: 0 };
                                if (prevState.hasNotes) {
                                  delete newState.hasNotes;
                                } else {
                                  newState.hasNotes = true;
                                }
                                return newState;
                              });
                            }}
                          />
                        }
                        label={<Typography variant="body2">{'Bewertungen mit Notizen'}</Typography>}
                      />
                    </FormGroup>
                  )}
                  {/*{ hasRole("can-use-deleted-reviews") && (
                    <FormControlLabel
                    control={
                    <Switch
                    checked={filter.isDeleted}
                  onChange={handleWithDeletedChange}
                  inputProps={{ "aria-label": "controlled" }}
                />
                }
                label={<Typography variant="body2">{"Gelöschte Bewertungen"}</Typography>}
              />
              )}*/}
                </FormGroup>
                <Divider sx={{ mt: theme.spacing(1) }} />
                <Button
                  disabled={loading}
                  variant="contained"
                  onClick={() => {
                    setPage(1);
                    loadAllReviews();
                  }}
                  sx={{ mt: theme.spacing(1) }}
                >
                  {t('apply')}
                </Button>
              </>
            }
            activeFilters={
              <>
                {filter.commentStatus && (
                  <Chip
                    label={`Status: ${filter.commentStatus}`}
                    onDelete={() => {
                      updateFilter((prevState) => {
                        const newState = { ...prevState, offset: 0 };
                        delete newState['commentStatus'];
                        return newState;
                      });
                    }}
                  />
                )}
                {filter.textSearch && (
                  <Chip
                    label={`Suche: ${StringUtils.fromFilterText(filter.textSearch)}`}
                    onDelete={() => {
                      updateFilter((prevState) => {
                        const newState = { ...prevState, offset: 0 };
                        delete newState['textSearch'];
                        return newState;
                      });
                      setPage(1);
                    }}
                  />
                )}
                {filter.company && (
                  <Chip
                    label={`${monitoring.find((m) => m.company === filter.company)?.label || ''}`}
                    onDelete={() => {
                      updateFilter((prevState) => {
                        const newState = { ...prevState, offset: 0 };
                        delete newState['company'];
                        return newState;
                      });
                    }}
                  />
                )}
                {(filter.score[0] !== 1 || filter.score[1] !== 5) && (
                  <Chip
                    label={`${t('stars')} ${filter.score[0]} - ${filter.score[1]}`}
                    onDelete={() => {
                      updateFilter((prevState) => ({ ...prevState, offset: 0, score: [1, 5] }));
                    }}
                  />
                )}
                {filter.startDate && isValid(new Date(filter.startDate)) && !filter.endDate && (
                  <Chip
                    label={`${t('date_from')}: ${format(new Date(filter.startDate), 'dd.MM.yyyy')}`}
                    onDelete={() => {
                      updateFilter((prevState) => {
                        const newState = { ...prevState, offset: 0 };
                        delete newState['startDate'];
                        return newState;
                      });
                      startDateInputRef.current = null;
                    }}
                  />
                )}
                {!filter.startDate && filter.endDate && isValid(new Date(filter.endDate)) && (
                  <Chip
                    label={`${t('date_to')}: ${format(new Date(filter.endDate), 'dd.MM.yyyy')}`}
                    onDelete={() => {
                      updateFilter((prevState) => {
                        const newState = { ...prevState, offset: 0 };
                        delete newState['endDate'];
                        return newState;
                      });
                      endDateInputRef.current = null;
                    }}
                  />
                )}
                {filter.startDate &&
                  isValid(new Date(filter.startDate)) &&
                  filter.endDate &&
                  isValid(new Date(filter.endDate)) && (
                    <Chip
                      label={`${format(new Date(filter.startDate), 'dd.MM.yyyy')} - ${format(
                        new Date(filter.endDate),
                        'dd.MM.yyyy'
                      )}`}
                      onDelete={() => {
                        updateFilter((prevState) => {
                          const newState = { ...prevState, offset: 0 };
                          delete newState['startDate'];
                          delete newState['endDate'];
                          return newState;
                        });
                        startDateInputRef.current = null;
                        endDateInputRef.current = null;
                      }}
                    />
                  )}
                {filter.platform && (
                  <Chip
                    label={platforms.find((p) => p.label === filter.platform)?.name || ''}
                    onDelete={() => {
                      updateFilter((prevState) => {
                        const newState = { ...prevState, offset: 0 };
                        delete newState['platform'];
                        delete newState['company'];
                        delete newState['kind'];
                        return newState;
                      });
                    }}
                  />
                )}
                {filter.kind && (
                  <Chip
                    label={reviewerRoles.find((f) => f.key === filter.kind)?.name || ''}
                    onDelete={() => {
                      updateFilter((prevState) => {
                        const newState = { ...prevState, offset: 0 };
                        delete newState['kind'];
                        return newState;
                      });
                    }}
                  />
                )}
                {filter.withComments && !filter.withoutComments && (
                  <Chip
                    label={t('reviews_with_comment')}
                    onDelete={() => {
                      updateFilter((prevState) => ({
                        ...prevState,
                        offset: 0,
                        withoutComments: true,
                      }));
                    }}
                  />
                )}
                {filter.withoutComments && !filter.withComments && (
                  <Chip
                    label={t('reviews_without_comment')}
                    onDelete={() => {
                      updateFilter((prevState) => ({
                        ...prevState,
                        offset: 0,
                        withComments: true,
                      }));
                    }}
                  />
                )}
                {filter.isUpdated && (
                  <Chip
                    label={t('updated_ratings')}
                    onDelete={() => {
                      updateFilter((prevState) => ({
                        ...prevState,
                        offset: 0,
                        isUpdated: true,
                      }));
                    }}
                  />
                )}
                {filter.hasNotes && (
                  <Chip
                    label={'Bewertungen mit Notizen'}
                    onDelete={() => {
                      updateFilter((prevState) => ({
                        ...prevState,
                        offset: 0,
                        hasNotes: false,
                      }));
                    }}
                  />
                )}
                {filter.isDeleted && (
                  <Chip
                    label={'Gelöschte Bewertungen'}
                    onDelete={() => {
                      updateFilter((prevState) => ({
                        ...prevState,
                        offset: 0,
                        isDeleted: true,
                      }));
                    }}
                  />
                )}
                {filter.withComments && filter.withoutComments && <Chip label={t('all_reviews')} />}
              </>
            }
            resetActiveFilters={() => {
              updateFilter(defaultFilter);
              startDateInputRef.current = null;
              endDateInputRef.current = null;
            }}
          />
        </RightContainer>
      </ContentContainer>
    </Section>
  );
};

export default Commentings;
